import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './AddChannelModal.module.scss';
import ModalWindow from '../../common/Modal';
import Button from '../../common/Button';
import MiraklButton from '../../common/MiraklButton';
import SearchableSelect from '../../common/SearchableSelect';
import { useDispatch } from 'react-redux';
import { getChannelRedirectUri } from '../../../store/actions/channels';

const integrationTypes = {
  mirakl: 'Mirakl',
  dsco: 'DSCO',
  commercehub: 'CommerceHub',
  logicbroker: 'Logic Broker',
  radial: 'Radial',
  revcascade: 'RevCascade',
  spscommerce: 'SPS Commerce',
  shopee: 'Shopee',
  tiktok: 'TikTok',
  amazon_sp: 'Amazon',
  shein: 'Shein',
};

const enabledConnectIntegrations = ['mirakl', 'shopee', 'lazada', 'tiktok', 'amazon_sp', 'shein'];

function AddChannelModal(props) {
  const { onClose, open, channels, onChannelSelect, showTiktokReturnTypeModal } = props;
  const dispatch = useDispatch();
  const modalRef = useRef(null);
  const [error, setError] = useState('');

  const isEnabledConnect = (channel) => {
    return channel ? enabledConnectIntegrations.includes(channel.integration_type) : false;
  };

  const isIntegrationChannel = useCallback(
    (channel, integrationName) => channel?.integration_type === integrationName,
    [],
  );
  const isMirakl = useCallback((channel) => isIntegrationChannel(channel, 'mirakl'), [isIntegrationChannel]);
  const isTikTok = useCallback((channel) => isIntegrationChannel(channel, 'tiktok'), [isIntegrationChannel]);

  const openTikTokReturnTypeModal = useCallback(
    (channel) => {
      onChannelSelect(channel);
      showTiktokReturnTypeModal(true);
    },
    [onChannelSelect, showTiktokReturnTypeModal],
  );

  const onSubmit = useCallback(
    (channelId) => {
      const channel = channels.find((c) => c.id === channelId);
      if (isEnabledConnect(channel)) {
        isTikTok(channel) ? openTikTokReturnTypeModal(channel) : dispatch(getChannelRedirectUri(channel));
      } else {
        setError('Please reach out to your account manager to setup this integration.');
        setTimeout(() => setError(''), 10000);
      }
    },
    [channels, dispatch, openTikTokReturnTypeModal, isTikTok],
  );

  const connectButton = useCallback(
    (channel) => {
      if (isMirakl(channel)) {
        return <MiraklButton size="small" onClick={() => onSubmit(channel.id)} />;
      }

      return <Button text="Connect" onClick={() => onSubmit(channel.id)} />;
    },
    [onSubmit, isMirakl],
  );

  const options = useMemo(() => {
    const options = channels.map((channel) => {
      const integrationType = integrationTypes[channel.integration_type] || channel.integration_type;
      return {
        key: `${integrationType}-${channel.handle}-${channel.id}`,
        value: `${channel.name} (${integrationType})`,
        image: channel.logo_path,
        button: connectButton(channel),
      };
    });
    options.sort((channelA, channelB) => {
      const valueA = channelA.value.toUpperCase();
      const valueB = channelB.value.toUpperCase();
      return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
    });

    return options;
  }, [channels, connectButton]);

  return (
    <ModalWindow open={open} onClose={onClose} center>
      <h2>Add Sales Channel</h2>
      <hr />

      <div className={styles.body} ref={modalRef}>
        <p>Please select the channel you want to add:</p>
        <div className={styles.channelSelect}>
          <SearchableSelect options={options} canSelect={false} />
        </div>
      </div>

      {error && (
        <div className={styles.footer}>
          <div className={styles.error}>{error}</div>
        </div>
      )}
    </ModalWindow>
  );
}

export default AddChannelModal;

AddChannelModal.defaultProps = {
  open: false,
};

AddChannelModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  channels: PropTypes.array,
  onChannelSelect: PropTypes.func,
  showTiktokReturnTypeModal: PropTypes.func,
};
